import { GoogleReCaptchaProvider } from '@google-recaptcha/react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';

import { MainErrorFallback } from '@/components/errors/main';
import ConfirmationDialog from '@/components/ui/confirmation-dialog';
import { Toaster } from '@/components/ui/sonner';
import { Spinner } from '@/components/ui/spinner';
import { Toaster as OldToaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SITE_KEY } from '@/config';
import { SignInDialog } from '@/global/auth/components/SignInDialog';
import { queryConfig } from '@/lib/react-query';
import { ThemeProvider } from '@/providers/theme-provider';

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  );

  return (
    <React.Suspense
      fallback={
        <div className="flex h-dvh w-screen items-center justify-center">
          <Spinner size="xl" />
        </div>
      }
    >
      <Sentry.ErrorBoundary fallback={MainErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <GoogleReCaptchaProvider siteKey={SITE_KEY} type="v3">
            <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
              <TooltipProvider delayDuration={500}>
                {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
                {children}
                <OldToaster />
                <Toaster />
                <SignInDialog />
                <ConfirmationDialog />
              </TooltipProvider>
            </ThemeProvider>
          </GoogleReCaptchaProvider>
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </React.Suspense>
  );
};
